import { useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { SERVICE_OPTIONS, PROPERTY_OPTIONS, COMPANY } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "",
  property_type: "",
  city: "",
  message: "",
};

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initial);
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="text-center py-12 px-6"
        data-testid="contact-success"
        style={{ background: "var(--cream)", border: "1px solid var(--line)" }}
      >
        <div
          className="mx-auto flex items-center justify-center rounded-full"
          style={{ width: 64, height: 64, background: "var(--gold)" }}
        >
          <Check size={32} color="#fff" />
        </div>
        <h3 className="font-display text-3xl mt-6">Merci !</h3>
        <p className="mt-3" style={{ color: "var(--muted)" }}>
          Votre demande a bien été envoyée. Nous vous répondons sous{" "}
          <strong>24h</strong>.
        </p>
        <button
          className="btn btn-outline-dark mt-7"
          onClick={() => setStatus("idle")}
          data-testid="contact-reset"
        >
          Nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Nom complet *</label>
          <input
            required
            className="field"
            value={form.name}
            onChange={set("name")}
            data-testid="contact-name"
            placeholder="Votre nom"
          />
        </div>
        <div>
          <label className="field-label">Email *</label>
          <input
            required
            type="email"
            className="field"
            value={form.email}
            onChange={set("email")}
            data-testid="contact-email"
            placeholder="vous@email.com"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Téléphone *</label>
          <input
            required
            className="field"
            value={form.phone}
            onChange={set("phone")}
            data-testid="contact-phone"
            placeholder="06 00 00 00 00"
          />
        </div>
        <div>
          <label className="field-label">Service souhaité *</label>
          <select
            required
            className="field"
            value={form.service}
            onChange={set("service")}
            data-testid="contact-service"
          >
            <option value="">Sélectionnez un service</option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Type de bien</label>
          <select
            className="field"
            value={form.property_type}
            onChange={set("property_type")}
            data-testid="contact-property"
          >
            <option value="">Sélectionnez un type</option>
            {PROPERTY_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Ville</label>
          <input
            className="field"
            value={form.city}
            onChange={set("city")}
            data-testid="contact-city"
            placeholder="Béziers, Cap d'Agde…"
          />
        </div>
      </div>

      <div>
        <label className="field-label">Votre message</label>
        <textarea
          className="field"
          rows={compact ? 3 : 4}
          value={form.message}
          onChange={set("message")}
          data-testid="contact-message"
          placeholder="Surface, nombre de lits, fréquence, options…"
        />
      </div>

      {status === "error" && (
        <p className="text-sm" style={{ color: "#c0392b" }} data-testid="contact-error">
          Une erreur est survenue. Réessayez ou appelez le {COMPANY.phone}.
        </p>
      )}

      <button
        type="submit"
        className="btn btn-primary w-full justify-center"
        disabled={status === "loading"}
        data-testid="contact-submit"
      >
        {status === "loading" ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Envoi…
          </>
        ) : (
          "Envoyer ma demande"
        )}
      </button>
      <p className="text-xs" style={{ color: "var(--muted)" }}>
        * Champs obligatoires. Nous répondons sous 24h.
      </p>
    </form>
  );
}
